import type { TrackConfig } from '@src/quince-components/src/hocs/withTracking'

/**
 * Optional helper that builds the product-card `PRODUCT_CLICK` track config shared by a section's
 * legacy and SVD branches, so the ~15-line context object is assembled once instead of per branch.
 *
 * This is a pure function, not a rendering choke-point — sections opt in (spread the result into a
 * `commonProps` object) or inline the config themselves. The section-varying bits are params;
 * per-card fields (product_id / product_color / col / productPosition) take precedence over
 * section-level `trackingContext` — this standardizes the merge order to match `FlatProductList`'s
 * existing convention. Note: `ImageWithProductCarousel`'s legacy branch previously spread
 * `product_id` / `product_color` / `col` *before* its section context, so section context could
 * override those three; routing it through this helper flips that to "per-card always wins". No live
 * event changes shape today (the only `trackingMetaData`-passing caller carries section-identity
 * fields, never per-card fields), but the standardized precedence is intentional going forward.
 * Returns `undefined` when tracking is disabled (no tracker), matching the previous inline
 * `tracker ? {…} : undefined`. `value` / `product_color` are conditional — some sections set them,
 * some don't, and the shape is preserved per section.
 */
export function buildProductCardClickConfig({
  enabled,
  action,
  category,
  value,
  label,
  spid,
  index,
  trackingContext,
  contentContainer,
  findingMethod,
  productColor,
}: {
  enabled: boolean
  action: string
  category: string
  value?: string
  label?: string
  spid: string | number
  index: number
  trackingContext?: Record<string, unknown>
  contentContainer: {
    contentContainerType?: string
    contentContainerName?: string
    contentContainerDisplayName?: string
  }
  findingMethod: string
  productColor?: string
}): TrackConfig | undefined {
  if (!enabled) return undefined
  return {
    trackInternal: true,
    trackGA: true,
    action,
    category,
    ...(value !== undefined ? { value } : {}),
    ...(label ? { label } : {}),
    context: {
      ...trackingContext,
      product_id: spid,
      ...(productColor ? { product_color: productColor } : {}),
      col: index,
      productPosition: index,
      contentContainerType: contentContainer.contentContainerType,
      contentContainerName: contentContainer.contentContainerName,
      contentContainerDisplayName: contentContainer.contentContainerDisplayName,
      findingMethod,
    },
  }
}
